<template>
    <div>
        <div class="row mb-5">
            <div class="col-3">
                <ui-select-search
                    @input="selectInsightTypes"
                    :label="$t('general.type_filter')"
                    name="type"
                    id="type"
                    multiple
                    :options="insightTypesOptions"
                />
            </div>

            <div
                v-if="insightTypes.includes('questionnaire')"
                class="col-3"
            >
                <ui-select-search
                    @input="selectQuestionnaireTypes"
                    :label="$t('general.questionnaire_filter')"
                    name="questionnaire_type"
                    id="questionnaire_type"
                    multiple
                    :options="questionnaireOptions"
                />
            </div>

            <div class="col-3">
                <button
                    v-model="toggleAll"
                    @click="toggleAll = !toggleAll"
                    type="button"
                    class="btn btn-primary mt-5"
                >
                    <span>
                        {{  toggleAll === true ? $t('general.collapse_all') : $t('general.expand_all') }}
                    </span>
                </button>
            </div>
        </div>

        <div
            v-for="(insightGroup, yearMonth) in groupedInsights"
            :key="yearMonth"
        >
            <ui-accordion
                class="card mb-5 border"
                :open="toggleAll === null ? yearMonth === firstGroupKey : toggleAll"
                :save="false"
                :key="`${yearMonth}-${toggleAll}}`"
            >
                <h2 slot="header" class="text-capitalize">
                    {{ new Date(yearMonth).toLocaleString(currentUserLocale(), { month: 'long', year: 'numeric' }) }}
                </h2>

                <table>
                    <tbody>
                        <tr
                            v-for="insight in insightGroup"
                            :key="$id(insight.id)"
                        >
                            <td class="text-uppercase">
                                <time :datetime="insight.created_at">
                                    {{ new Date(insight.created_at).toLocaleDateString(currentUserLocale(), { year: 'numeric', month: 'long', day: 'numeric' }) }}
                                </time>
                            </td>
                            <td>
                                {{ insight.note }}

                                <template v-if="insight.meta && Object.keys(insight.meta).includes('kanban_status_new')">
                                    <br v-if="insight.note !== null">
                                    <br v-if="insight.note !== null">

                                    {{ $t('patients.status_changed_from') }}
                                    <span class="badge badge-secondary mx-2">{{ $t(`patients.kanban_titles.${insight.meta.kanban_status_old}`) }}</span>
                                    {{ $t('patients.status_changed_to') }} <span class="badge badge-secondary mx-2">{{ $t(`patients.kanban_titles.${insight.meta.kanban_status_new}`) }}</span>
                                </template>

                                <template v-if="insight.meta && Object.keys(insight.meta).includes('pregnancy_status_new')">
                                    <br v-if="insight.note !== null">
                                    <br v-if="insight.note !== null">

                                    {{ $t('patients.pregnancy_status_changed_from') }}
                                    <span class="badge badge-secondary mx-2">{{ $t(`general.${insight.meta.pregnancy_status_old}`) }}</span>
                                    {{ $t('patients.status_changed_to') }} <span class="badge badge-secondary mx-2">{{ $t(`general.${insight.meta.pregnancy_status_new}`) }}</span>
                                </template>

                            </td>
                            <td>
                                <div
                                    v-if="insight.meta && insight.meta.questionnaire_id"
                                    class="d-flex align-items-center justify-content-end m-n2"
                                >
                                    <a
                                        :href="route('hospitals.patients.questionnaires.show', [
                                            hospitalId,
                                            patientId,
                                            insight.meta.questionnaire_id,
                                        ])"
                                        class="btn btn-primary btn-symbol m-2"
                                        :aria-label="$t('general.to_detail')"
                                        :content="$t('general.to_detail')"
                                        v-tippy
                                    >
                                        <ui-icon
                                            icon="arrow-right"
                                        />
                                    </a>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </ui-accordion>
        </div>
    </div>
</template>

<script>
import UiSelectSearch from '../ui/select-search.vue';
import UiAccordion from '../ui/accordion.vue';
import UiIcon from '../ui/icon.vue';

export default {
    name: 'PatientInsights',
    components: {
        UiSelectSearch,
        UiAccordion,
        UiIcon,
    },
    props: {
        patientId: {
            type: Number,
            required: true,
        },
        hospitalId: {
            type: Number,
            required: true,
        },
        insights: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            insightTypes: [],
            questionnaireTypes: [],
            toggleAll: null,
        };
    },
    computed: {
        filteredInsights() {
            if (this.insightTypes.length === 0) {
                return this.insights;
            }

            return this.insights.filter(insight => {
                if (
                    this.insightTypes.includes('questionnaire')
                    && this.questionnaireTypes.length > 0
                ) {
                    return insight.type === 'questionnaire'
                        && insight.meta !== null
                        && this.questionnaireTypes.includes(insight.meta.questionnaire_type);
                }

                return this.insightTypes.includes(insight.type);
            });
        },

        groupedInsights() {
            return this.filteredInsights
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .reduce((acc, insight) => {
                    const date = new Date(insight.created_at);
                    const key = `${date.getFullYear()}-${date.getMonth() + 1}`;

                    if (!acc[key]) {
                        acc[key] = [];
                    }

                    acc[key].push(insight);

                    return acc;
                }, {});
        },

        firstGroupKey() {
            return Object.keys(this.groupedInsights)[0] ?? '';
        },

        questionnaireOptions() {
            return this.insights.reduce((acc, insight) => {
                if (
                    insight.type !== 'questionnaire'
                    || insight.meta === null
                    || acc.some((accInsight) => {
                        return accInsight.name === insight.meta.questionnaire_type;
                    })
                ) {
                    return acc;
                }

                acc.push({
                    name: insight.meta.questionnaire_type,
                    label: insight.meta.questionnaire_type,
                    value: insight.meta.questionnaire_type,
                    id: insight.meta.questionnaire_type,
                });

                return acc;
            }, []).sort((a, b) => a.name.localeCompare(b.name));
        },

        insightTypesOptions() {
            return this.insights.reduce((acc, insight) => {
                if (
                    insight.type === null
                    || acc.some((accInsight) => {
                        return accInsight.value === insight.type;
                    })
                ) {
                    return acc;
                }

                acc.push({
                    name: this.$t(`patients.insights.${insight.type}`),
                    label: this.$t(`patients.insights.${insight.type}`),
                    id: insight.type,
                    value: insight.type,
                });

                return acc;
            }, []).sort((a, b) => a.name.localeCompare(b.name));
        },
    },
    methods: {
        selectInsightTypes(value) {
            this.insightTypes = value.map(option => option.value);
        },

        selectQuestionnaireTypes(value) {
            this.questionnaireTypes = value.map(option => option.value);
        },
    },
}
</script>
